// @ts-strict-ignore
import {AttrsSchema} from 'panel';

import {ButtonLayout, ButtonSize, ButtonTheme, ButtonType} from 'common/components/button/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import type {TooltipAttrs} from 'common/components/tooltip/types';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {SimpleNavManager} from 'common/contexts/nav-manager/simple-nav-manager';

import {BaseButtonAttrs, baseButtonParams, ButtonBase} from '../base/button';
import {template} from './template';
import css from './index.inline.styl';

export interface ButtonJsxProps {
  attrs?: ButtonAttrs;
  params?: ButtonParams;
  on?: {
    click?: (ev: MouseEvent) => void;
  };
}

type Attrs = {
  class?: string;
  'highlight-icon'?: boolean;
  icon?: string;
  size?: ButtonSize;
  theme?: ButtonTheme;
  type?: ButtonType;
  pressed?: boolean;
  tooltip?: TooltipAttrs;
  layout?: ButtonLayout;
  'experimental-positioning'?: boolean;
  'disable-pressed-styles'?: boolean;
  'disabled-stays-opaque'?: boolean;
  href?: string;
  download?: string;
  target?: string;
};

type ButtonParams = {
  tooltip?: TooltipAttrs;
};

export type ButtonAttrs = BaseButtonAttrs & Attrs;

export const buttonParams: {[key in keyof ButtonAttrs]} = {
  ...baseButtonParams,
  class: String,
  'highlight-icon': Boolean,
  icon: String,
  size: String,
  theme: String,
  type: String,
  pressed: Boolean,
  tooltip: Object,
  layout: String,
  href: String,
  target: String,
  download: String,
};

export class Button extends ButtonBase<Attrs, ButtonParams, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<ButtonAttrs> {
    return {
      ...super.attrsSchema,
      class: `string`,
      'highlight-icon': `boolean`,
      icon: `string`,
      size: {type: `string`, enum: Object.values(ButtonSize), default: ButtonSize.Medium},
      theme: {type: `string`, enum: Object.values(ButtonTheme), default: ButtonTheme.Secondary},
      type: {type: `string`, enum: Object.values(ButtonType), default: ButtonType.Info},
      layout: {type: `string`, enum: Object.values(ButtonLayout), default: ButtonLayout.Horizontal},
      pressed: `boolean`,
      'experimental-positioning': `boolean`,
      tooltip: `json`,
      'disable-pressed-styles': `boolean`,
      'disabled-stays-opaque': `boolean`,
      href: `string`,
      target: `string`,
      download: `string`,
    };
  }

  get config() {
    return Object.assign(super.config, {
      css,
      template,
      contexts: [`navManager` as const],
      defaultContexts: {
        navManager: new SimpleNavManager(),
      },
      helpers: {
        ...super.config.helpers,
        ButtonType,
      },
      params: {
        tooltip: Object,
      },
      hooks: {
        preUpdate: () => {
          // Update accessible label if icon changes
          if (this.isIconOnly() && this.getAttribute(`aria-label`) !== this.attr(`icon`)) {
            this.setAttribute(`aria-label`, this.attr(`icon`));
          }
        },
      },
    });
  }
  connectedCallback(): void {
    super.connectedCallback();
    // Add accessible label for icon-only buttons
    if (this.isIconOnly()) {
      this.setAttribute(`aria-label`, this.attr(`icon`));
    }
  }

  getContent() {
    return this.innerHTML.trim();
  }

  handleClick = (ev: MouseEvent) => {
    if (this.attr(`disabled`)) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
    } else if (this.attr(`href`) && !this.attr(`download`)) {
      this.getContext(`navManager`).navigate(this.attr(`href`), {
        triggerEvent: ev,
        fromAnchorLink: true,
        anchorTarget: this.attr(`target`),
      });
    }
  };

  isIconOnly() {
    const content = this.getContent();
    return (!content && !!this.attr(`icon`)) || content.length === 1;
  }
}

export const ButtonEl = defineAndMakeJsxFactory<ButtonJsxProps>(`mp-button`, Button);
export const ButtonReact = panel2react<ButtonJsxProps>(`mp-button`, Button);
